import classNames from 'classnames';
import React, { useState } from 'react';
import { useCreateStatementPdf } from '../../../hooks/apolloHooks/useCreateStatementPdf';
import { CardStatement } from '../../../integrations/allkort/graphql/interface/Customer';
import { ImportIcon, InfoIconOutlined } from '../../svgs/ExpressonSVGs';
import Tooltip from '../Tooltip';

type StatementGridSingleProps = {
    wrapperClasses?: string;
    statement: CardStatement;
}

const StatementGridSingle: React.FC<StatementGridSingleProps> = ({
    wrapperClasses = '',
    statement
}) => {

    const [statementPdfUrl, setStatementPdfUrl] = useState<string>('');
    const { createStatementPdf, loading: pdfLoading } = useCreateStatementPdf();

    // Handle action button click - fetch PDF and open in new tab
    const handleDownloadClick = async (e: React.MouseEvent | React.KeyboardEvent) => {
        e.preventDefault();
        e.stopPropagation();

        // If we already have the URL, open it
        if (statementPdfUrl) {
            window.open(statementPdfUrl, '_blank', 'noopener,noreferrer');
            return;
        }

        // Fetch the PDF URL
        try {
            const result = await createStatementPdf(statement.id);
            if (result?.url) {
                setStatementPdfUrl(result.url);
                // Open the URL immediately after fetching
                window.open(result.url, '_blank', 'noopener,noreferrer');
            }
        } catch (err) {
            console.error('Failed to fetch statement PDF URL:', err);
        }
    };

    // Format date to Swedish locale
    const formatDate = (dateString: string | null): string => {
        if (!dateString) return '-';
        const date = new Date(dateString).toLocaleDateString('sv-SE');
        return date;
    };

    // Format amount as currency
    const formatAmount = (amount: number | null): string => {
        if (amount === null || amount === undefined) return '0 kr';
        return (amount || 0).toLocaleString('sv-SE', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' kr';
    };

    // Format period as "15 sep. - 15 okt."
    const formatPeriod = (dateString: string | null): string => {
        if (!dateString) return '-';
        const endDate = new Date(dateString);
        endDate.setDate(endDate.getDate());

        // Previous month date (same day as original statement date, previous month)
        const startDate = new Date(dateString);
        startDate.setMonth(startDate.getMonth() - 1);
        startDate.setDate(16);

        // Format with Swedish short month names
        const startFormatted = startDate.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' });
        const endFormatted = endDate.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' });

        return `${startFormatted} - ${endFormatted}`;
    };


    return (
        <div
            className={classNames(
                'statement-grid-single',
                wrapperClasses
            )}
            id={`statement-${statement.id}`}
        >
            <div className="row-statementnr" data-label="Kontobeskeds-ID">
                <label>Kontobesked</label>
                <span>{statement.id}</span>
            </div>

            <div className="row-info">
                <Tooltip text="Klicka på visa för mer information om kontobeskedet" position="top-end">
                    <InfoIconOutlined width="26" height="26" />
                </Tooltip>
            </div>

            <div className="row-date" data-label="Datum">
                <label>Datum</label>
                <span>{formatDate(statement.statementDate)}</span>
            </div>

            <div className="row-amount" data-label="Summa">
                <label>Periodens inköp</label>
                <span>{formatAmount(statement.purchase)}</span>
            </div>
            <div className="divider"></div>
            <div className="bottom-row">
                <span>Period {formatPeriod(statement.statementDate)}</span>
                <div
                    className={classNames("action", { "loading": pdfLoading })}
                    data-label="Åtgärd"
                    onClick={handleDownloadClick}
                    style={{ cursor: 'pointer' }}
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            handleDownloadClick(e);
                        }
                    }}
                >
                    {pdfLoading ? '...' : 'Visa'}<ImportIcon width="18" height="18" />
                </div>
            </div>

        </div>
    )
}

export default StatementGridSingle
